/* eslint-disable no-nested-ternary */
import PropTypes from 'prop-types';
import React, { useContext } from 'react';
import {
  Button,
  ButtonGroup,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  Spinner,
  useToast,
  useBreakpointValue,
  useModalContext,
} from '@chakra-ui/react';
import { BiDotsHorizontalRounded } from 'react-icons/bi';

const getActionColorScheme = (action) => {
  if (action.positive) return 'green';
  if (action.negative) return 'red';
  return null;
};

const Trigger = ({ size }) => (
  <IconButton
    aria-label="more..."
    icon={<BiDotsHorizontalRounded />}
    size={size}
  />
);
Trigger.propTypes = {
  size: PropTypes.string,
};

/*
 * There is a weird issue when rendering a portal inside a modal
 * see: https://github.com/chakra-ui/chakra-ui/issues/6794
 *
 * So we make a Magic portal that finds if it's inside a modal and sets the `containerRef` appropriately.
 * (Why doesn't Chakra do this already?)
 */

const MagicPortal = ({ children }) => {
  try {
    const modalContext = useModalContext();
    return <Portal containerRef={modalContext?.dialogRef}>{children}</Portal>;
  } catch {
    return <Portal>{children}</Portal>;
  }
};

const ActionType = PropTypes.shape({
  label: PropTypes.string,
  action: PropTypes.func,
  hidden: PropTypes.bool,
  icon: PropTypes.string,
  negative: PropTypes.bool,
  positive: PropTypes.bool,
  extraProps: PropTypes.object,
});

const MenuContext = React.createContext({
  setLoading: () => undefined,
});

const OverflowMenuItem = ({ action, context }) => {
  const [loading, setLoading] = React.useState(false);
  const menuContext = useContext(MenuContext);
  const toast = useToast();

  const onClick = action.action
    ? () => {
      const result = action.action(undefined, { context });
      if (result?.then) {
        setLoading(true);
        menuContext.setLoading(true);
        result.then(
          () => {
            setLoading(false);
            menuContext.setLoading(false);
          },
          (error) => {
            setLoading(false);
            menuContext.setLoading(false);
            toast({
              status: 'error',
              title: `${action.label} error`,
              description: error?.message,
              isClosable: true,
            });
          });
      }
      return result;
    }
    : undefined;

  return (
    <MenuItem
      onClick={onClick}
      context={context}
      isDisabled={!action.extraProps && !action.action}
      icon={loading ? <Spinner /> : action.icon}
      color={getActionColorScheme(action)}
      {...action.extraProps}
    >
      {action.label}
    </MenuItem>
  );
};

const OverflowMenu = ({ actions, open, size, context }) => {
  const [loading, setLoading] = React.useState(false);
  return (
    <MenuContext.Provider value={{ setLoading }}>
      <Menu defaultIsOpen={open}>
        <MenuButton
          as={IconButton}
          icon={loading ? <Spinner /> : <BiDotsHorizontalRounded />}
          size={size}
        />
        <MagicPortal>
          <MenuList>
            {actions.map((a, index) => (
              <OverflowMenuItem action={a} context={context} key={index}></OverflowMenuItem>
            ))}
          </MenuList>
        </MagicPortal>
      </Menu>
    </MenuContext.Provider>
  );
};

OverflowMenu.propTypes = {
  actions: PropTypes.arrayOf(ActionType),
  open: PropTypes.bool,
  size: PropTypes.string,
  context: PropTypes.object,
};

const PrimaryButton = ({ action: ac, buttonSize, context }) => {
  const Component = ac.label ? Button : IconButton;

  const colorScheme = getActionColorScheme(ac) || 'blue';

  return (
    <Component
      onClick={ac.action ? () => ac.action(undefined, { context }) : undefined}
      colorScheme={colorScheme}
      {...(ac.label ? { leftIcon: ac.icon } : { icon: ac.icon })}
      positive={ac.positive}
      negative={ac.negative}
      size={buttonSize}
      context={context}
      {...ac.extraProps}
    >
      {ac.label}
    </Component>
  );
};

PrimaryButton.propTypes = {
  action: ActionType,
  buttonSize: PropTypes.string,
  context: PropTypes.object,
};

const ActionMenu = ({
  actions,
  open,
  size,
  suppressPrimary,
  context,
  className,
  loading,
}) => {
  const isSmallScreen = useBreakpointValue({ base: true, md: false });

  if (!actions || !actions.length) return null;

  const filteredActions = actions.filter((a) => !a.hidden);

  if (!filteredActions.length) return null;

  const isLoading = loading || filteredActions.some((a) => a.loading);

  const primaryAction = filteredActions[0];
  const overflowActions = filteredActions.slice(1);

  const buttonSize = size;

  const singleButton = !overflowActions.length;

  if (suppressPrimary) {
    return (
      <div className={className}>
        <OverflowMenu
          actions={filteredActions}
          open={open}
          size={buttonSize}
          context={context}
          loading={isLoading}
        />
      </div>
    );
  }

  if (singleButton) {
    return (
      <div className={className}>
        <PrimaryButton action={primaryAction} buttonSize={buttonSize} context={context} />
      </div>
    );
  }

  if (isSmallScreen) {
    return (
      <div className={className}>
        <OverflowMenu actions={filteredActions} open={open} size={buttonSize} context={context} />
      </div>
    );
  }

  return (
    <ButtonGroup className={className} spacing="0.5em">
      <PrimaryButton action={primaryAction} buttonSize={buttonSize} context={context} />
      <OverflowMenu actions={overflowActions} open={open} size={buttonSize} context={context} />
    </ButtonGroup>
  );
};

ActionMenu.propTypes = {
  actions: PropTypes.arrayOf(ActionType),
  open: PropTypes.bool,
  size: PropTypes.string,
  context: PropTypes.object,
  suppressPrimary: PropTypes.bool,
  className: PropTypes.string,
  loading: PropTypes.bool,
};

export default ActionMenu;
